/**
 * SongPanel - Interface pour le mode Song
 *
 * Permet de:
 * - Gérer les steps du song (scene + nombre de bars)
 * - Réordonner les steps
 * - Visualiser la position courante pendant la lecture
 *
 * Note: Le Song mode utilise maintenant des Scenes (et non plus des patterns globaux).
 * Chaque step du song lance une scene qui définit les patterns par instrument.
 */

export class SongPanel {
    constructor(store) {
        this.store = store;
        this.container = null;
        this.isCollapsed = localStorage.getItem('songtimeline_collapsed') === 'true';

        // S'abonner aux changements de song
        this.unsubscribeSong = this.store.onSongChange(() => {
            this.updateDisplay();
        });

        // S'abonner aux changements de scene pour mettre à jour l'affichage
        this.unsubscribeScene = this.store.onSceneChange(() => {
            this.updateDisplay();
        });
    }

    render(container) {
        if (typeof container === 'string') {
            this.container = document.getElementById(container);
        } else {
            this.container = container;
        }

        if (!this.container) return;

        this.updateDisplay();
    }

    updateDisplay() {
        if (!this.container) return;

        const song = this.store.getSong();
        const songMode = this.store.isSongMode();
        const position = this.store.getSongPosition();
        const scenes = this.store.getScenes();
        const sceneIds = Object.keys(scenes);

        this.container.innerHTML = `
            <div class="machine songtimeline-machine ${this.isCollapsed ? 'collapsed' : ''}" id="songtimeline_machine">
                <div class="machine-title">
                    <button class="collapse-btn" id="songtimeline_collapse" title="Réduire/Agrandir">${this.isCollapsed ? '▶' : '▼'}</button>
                    SONG TIMELINE <span class="brand">Sequence</span>
                </div>
                <div class="machine-content ${this.isCollapsed ? 'hidden' : ''}" id="songtimeline_content">
                    <div class="song-panel ${songMode ? 'song-active' : ''}">
                        <div class="song-steps ${songMode ? 'enabled' : 'disabled'}">
                            ${song.map((step, index) => {
                                const isCurrent = songMode && index === position.stepIndex;
                                const sceneName = step.sceneId && scenes[step.sceneId]
                                    ? scenes[step.sceneId].name
                                    : 'Unknown';
                                return `
                                    <div class="song-step ${isCurrent ? 'current' : ''}" data-index="${index}">
                                        <span class="step-num">${index + 1}.</span>
                                        <select class="scene-select" data-index="${index}">
                                            ${sceneIds.map(id => `
                                                <option value="${id}" ${step.sceneId === id ? 'selected' : ''}>${scenes[id].name}</option>
                                            `).join('')}
                                        </select>
                                        <span class="bars-label">x</span>
                                        <select class="bars-select" data-index="${index}">
                                            ${[1, 2, 4, 8, 16].map(bars => `
                                                <option value="${bars}" ${step.bars === bars ? 'selected' : ''}>${bars}</option>
                                            `).join('')}
                                        </select>
                                        <span class="bars-unit">bars</span>
                                        ${isCurrent ? `<span class="progress-indicator">(${position.bar + 1}/${step.bars})</span>` : ''}
                                        <div class="step-actions">
                                            <button class="step-btn move-up" data-index="${index}" ${index === 0 ? 'disabled' : ''}>▲</button>
                                            <button class="step-btn move-down" data-index="${index}" ${index === song.length - 1 ? 'disabled' : ''}>▼</button>
                                            <button class="step-btn delete-step" data-index="${index}" ${song.length <= 1 ? 'disabled' : ''}>✕</button>
                                        </div>
                                    </div>
                                `;
                            }).join('')}
                        </div>

                        <div class="song-footer">
                            <button class="add-step-btn" id="addSongStep">+ Add Step</button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Re-attach event listeners after updating DOM
        this.setupEventListeners();
    }

    setupEventListeners() {
        if (!this.container) return;

        // Collapse button
        const collapseBtn = this.container.querySelector('#songtimeline_collapse');
        if (collapseBtn) {
            collapseBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleCollapse();
            });
        }

        // Scene selects
        this.container.querySelectorAll('.scene-select').forEach(select => {
            select.addEventListener('change', (e) => {
                const index = parseInt(e.target.dataset.index);
                const song = this.store.getSong();
                const sceneId = e.target.value;
                const bars = song[index].bars;
                this.store.updateSongStep(index, sceneId, bars);
            });
        });

        // Bars selects
        this.container.querySelectorAll('.bars-select').forEach(select => {
            select.addEventListener('change', (e) => {
                const index = parseInt(e.target.dataset.index);
                const song = this.store.getSong();
                const sceneId = song[index].sceneId;
                const bars = parseInt(e.target.value);
                this.store.updateSongStep(index, sceneId, bars);
            });
        });

        // Move up buttons
        this.container.querySelectorAll('.move-up').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const index = parseInt(e.target.dataset.index);
                this.store.moveSongStepUp(index);
            });
        });

        // Move down buttons
        this.container.querySelectorAll('.move-down').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const index = parseInt(e.target.dataset.index);
                this.store.moveSongStepDown(index);
            });
        });

        // Delete buttons
        this.container.querySelectorAll('.delete-step').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const index = parseInt(e.target.dataset.index);
                this.store.removeSongStep(index);
            });
        });

        // Add step button
        const addBtn = this.container.querySelector('#addSongStep');
        if (addBtn) {
            addBtn.addEventListener('click', () => {
                // Use the first scene by default, or current scene if available
                const currentSceneId = this.store.getCurrentSceneId();
                const sceneIds = Object.keys(this.store.getScenes());
                const sceneId = currentSceneId || sceneIds[0];
                this.store.addSongStep(sceneId, 4);
            });
        }
    }

    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
        localStorage.setItem('songtimeline_collapsed', this.isCollapsed);

        const machine = this.container.querySelector('#songtimeline_machine');
        const content = this.container.querySelector('#songtimeline_content');
        const btn = this.container.querySelector('#songtimeline_collapse');

        if (this.isCollapsed) {
            machine.classList.add('collapsed');
            content.classList.add('hidden');
            btn.textContent = '▶';
        } else {
            machine.classList.remove('collapsed');
            content.classList.remove('hidden');
            btn.textContent = '▼';
        }
    }

    destroy() {
        if (this.unsubscribeSong) {
            this.unsubscribeSong();
        }
        if (this.unsubscribeScene) {
            this.unsubscribeScene();
        }
    }
}
